import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SEOHead, { getServiceSchema, getBreadcrumbSchema } from '@/components/shared/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { servicesData, companyInfo } from '@/data/mock';
import { ArrowRight, CheckCircle2, Phone, ChevronRight, Wrench, Target, Lightbulb, FileText, Building, Quote } from 'lucide-react';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const breadcrumbItems = [
    { name: 'Home', url: 'https://redspire.com' },
    { name: 'Services', url: 'https://redspire.com/services' },
    { name: service.title, url: `https://redspire.com/services/${service.id}` }
  ];

  const combinedSchema = [
    getServiceSchema(service),
    getBreadcrumbSchema(breadcrumbItems)
  ];

  return (
    <Layout>
      <SEOHead
        title={service.seo.title}
        description={service.seo.description}
        keywords={service.seo.keywords}
        image={service.heroImage}
        url={`https://redspire.com/services/${service.id}`}
        schema={combinedSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={`${service.title} - Civil engineering services`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/services" className="hover:text-white">Services</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-red-400">{service.title}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {service.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
              {service.shortDescription}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-600" />
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {service.overview}
              </p>
            </div>
            <div>
              <Card className="border-0 shadow-lg bg-gray-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Ready to discuss your project? Our team is here to help.
                  </p>
                  <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-red-600 font-medium mb-4">
                    <Phone className="w-4 h-4" />
                    {companyInfo.phone}
                  </a>
                  <Link to="/contact">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                      Request a Consultation
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What This Service Includes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-red-600" />
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Scope</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Service Includes</h2>
              <ul className="space-y-4">
                {service.whatIncludes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src={service.images[0]?.src}
                alt={service.images[0]?.alt || `${service.title} project example`}
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-red-600 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Target className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Expertise</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Core Capabilities</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {service.coreCapabilities.map((capability, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-3 h-12 bg-red-600 rounded-full mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{capability.title}</h3>
                  <p className="text-gray-600">{capability.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Approach */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb className="w-6 h-6 text-red-500" />
                <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Methodology</span>
              </div>
              <h2 className="text-3xl font-bold mb-6">Our Technical Approach</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                {service.technicalApproach}
              </p>
            </div>
            <div className="relative">
              <img
                src={service.images[1]?.src || service.heroImage}
                alt={service.images[1]?.alt || `${service.title} technical approach`}
                className="rounded-2xl shadow-xl w-full aspect-square object-cover"
              />
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-red-600/20 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Tools and Methods */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Wrench className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Technology</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Tools, Methods & Technologies</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {service.toolsAndMethods.map((tool, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
              >
                {tool}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Building className="w-6 h-6 text-red-600" />
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Applications</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Typical Project Types</h2>
              <ul className="space-y-4">
                {service.projectTypes.map((type, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <ArrowRight className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700">{type}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="border-0 shadow-xl bg-white overflow-hidden">
                <div className="h-2 bg-red-600" />
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-red-100 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {service.caseExample.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.caseExample.description}
                  </p>
                  <Badge className="mt-4 bg-red-100 text-red-700 hover:bg-red-100">
                    Case Example
                  </Badge>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why This Service Matters</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {service.whyMatters}
            </p>
          </div>
        </div>
      </section>

      {/* How Redspire Delivers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              How Redspire Delivers This Service
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed text-center">
              {service.howDelivers}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your {service.title} Project?
          </h2>
          <p className="text-lg text-red-100 max-w-2xl mx-auto mb-10">
            Contact Redspire today to discuss how our {service.title.toLowerCase()} services can support your infrastructure goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 group">
                Request a Proposal
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white bg-transparent hover:bg-white/10 px-8">
                <Phone className="mr-2 w-5 h-5" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetailPage;
